import React, { ReactNode, useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { faCog } from "@fortawesome/pro-solid-svg-icons/faCog";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Col from "antd/es/col";
import Dropdown from "antd/es/dropdown";
import AntDMenu from "antd/es/menu";
import Row from "antd/es/row";
import Switch from "antd/es/switch";
import { useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared/enum";
import PermissionName from "@mapmycustomers/shared/enum/PermissionName";
import Deal from "@mapmycustomers/shared/types/entity/Deal";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import ColumnModel from "@mapmycustomers/shared/types/viewModel/internalModel/ColumnModel";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button } from "@mapmycustomers/ui";

import DownloadEntitiesButton from "@app/component/DownloadEntitiesModal/DownloadEntitiesButton";
import SelectColumnsButton from "@app/component/SelectColumnsPane/SelectColumnsButton";
import { skipNonListViewFields } from "@app/component/SelectColumnsPane/util/fieldFilter";
import useView from "@app/component/view/utils/useView";
import ViewMode from "@app/scene/deal/enum/ViewMode";
import { hasPermission, isCurrentUserOwner } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import layout from "@app/styles/layout";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { isSimpleCondition } from "@app/util/viewModel/assert";

import { getFunnelChartHidden } from "../../store";
import { downloadDealData, switchView, toggleFunnelChartDisplay } from "../../store/actions";
import ManageDealLossReasonModal from "../ManageDealLossReasonModal";
import ManageFunnelsModal from "../ManageFunnelsModal";

import styles from "./TopPanel.module.scss";
import ViewModeSelector from "./ViewModeSelector";

interface Props {
  canDownload: boolean;
  downloadDealData: typeof downloadDealData;
  extra?: ReactNode[];
  filterButton?: ReactNode;
  isFunnelChartHidden: boolean;
  isOwner: boolean;
  onSwitchView: typeof switchView;
  toggleFunnelChartDisplay: typeof toggleFunnelChartDisplay;
  viewMode: ViewMode;
}

const Toolbar: React.FC<Props> = ({
  canDownload,
  downloadDealData,
  extra,
  filterButton = [],
  isFunnelChartHidden,
  isOwner,
  onSwitchView,
  toggleFunnelChartDisplay,
  viewMode,
}) => {
  const analyticIssuer = useAnalytics();
  const {
    viewState: { columns, filter },
  } = useView<Deal>();
  const intl = useIntl();
  const handleToggleFunnelChartDisplay = useCallback(
    (value: boolean) => {
      toggleFunnelChartDisplay(!value);
    },
    [toggleFunnelChartDisplay]
  );
  const handleChangeViewMode = useCallback(
    (newViewMode: ViewMode) => {
      onSwitchView({ from: viewMode, to: newViewMode });
    },
    [onSwitchView, viewMode]
  );

  const [funnelModalVisible, showFunnelModal, hideFunnelModal] = useBoolean();
  const [dealLostReasonModalVisible, showDealLostReasonModal, hideDealLostReasonModal] =
    useBoolean();

  const selectedFunnelIds: Array<Funnel["id"]> = useMemo(() => {
    if (!filter.funnel || !isSimpleCondition(filter.funnel)) {
      return [];
    }

    return filter.funnel.value;
  }, [filter]);

  const handleShowFunnelModal = useCallback(() => {
    showFunnelModal();
    analyticIssuer?.clicked(["Manage Funnels and Stages"]);
  }, [analyticIssuer, showFunnelModal]);

  const handleShowDealLostReasonModal = useCallback(() => {
    showDealLostReasonModal();
    analyticIssuer?.clicked(["Manage Deal Lost Reasons"]);
  }, [analyticIssuer, showDealLostReasonModal]);

  const settingOptionVisible = isOwner || viewMode === ViewMode.LIST;
  const dealSettingOptions = (
    <AntDMenu>
      {isOwner && (
        <AntDMenu.Item key="funnelSettings" onClick={handleShowFunnelModal}>
          {intl.formatMessage({
            id: "deal.topPanel.settings.funnelSettings",
            defaultMessage: "Manage funnels and stages",
            description: "Funnel and stage option in deal settings",
          })}
        </AntDMenu.Item>
      )}
      {isOwner && (
        <AntDMenu.Item key="manageLossReasons" onClick={handleShowDealLostReasonModal}>
          {intl.formatMessage({
            id: "deal.topPanel.settings.manageLossReason",
            defaultMessage: "Manage loss reasons",
            description: "Manage loss reason in deal settings",
          })}
        </AntDMenu.Item>
      )}
      {viewMode === ViewMode.LIST && (
        <>
          {selectedFunnelIds.length === 1 && (
            <AntDMenu.Item key="showFunnelChart">
              <div className={styles.toggleShowFunnelChartMenuItem}>
                {intl.formatMessage({
                  id: "deal.topPanel.settings.showFunnelChart",
                  defaultMessage: "Show funnel graphic",
                  description: "Show funnel chart option in deal settings",
                })}
                <Switch checked={!isFunnelChartHidden} onChange={handleToggleFunnelChartDisplay} />
              </div>
            </AntDMenu.Item>
          )}
        </>
      )}
    </AntDMenu>
  );

  const handledDownload = useCallback(
    (columns: ColumnModel) => {
      downloadDealData({ columns, viewMode });
      analyticIssuer?.clicked(["Export Table Data"]);
    },
    [analyticIssuer, downloadDealData, viewMode]
  );

  return (
    <Row align="middle" className={styles.container} justify="space-between">
      <Col>
        <Row gutter={layout.spacerM}>
          <Col>
            <ViewModeSelector onChange={handleChangeViewMode} value={viewMode} />
          </Col>
          {filterButton && <Col>{filterButton}</Col>}
        </Row>
      </Col>
      <Col>
        <Row gutter={layout.spacerS}>
          {extra && extra.map((node, index) => <Col key={index}>{node}</Col>)}
          {settingOptionVisible && (
            <Col>
              <Dropdown overlay={dealSettingOptions} placement="bottomRight" trigger={["click"]}>
                <Button icon={<FontAwesomeIcon icon={faCog} />} iconOnly type="text" />
              </Dropdown>
            </Col>
          )}
          {viewMode === ViewMode.LIST && (
            <Col>
              <SelectColumnsButton
                entityType={EntityType.DEAL}
                fieldFilter={skipNonListViewFields}
              />
            </Col>
          )}
          {canDownload && (
            <Col>
              <DownloadEntitiesButton
                defaultColumnList={viewMode === ViewMode.LIST ? columns : undefined}
                entityType={EntityType.DEAL}
                hasAdditionalFilters={viewMode !== ViewMode.LIST}
                informationPoint={intl.formatMessage({
                  id: "deal.topPanel.download.point1",
                  defaultMessage: "All deals in view",
                  description: "Deal toolbar - download all - information point 1",
                })}
                onDownload={handledDownload}
              />
            </Col>
          )}
        </Row>
      </Col>
      {funnelModalVisible && <ManageFunnelsModal onHide={hideFunnelModal} />}
      {dealLostReasonModalVisible && <ManageDealLossReasonModal onHide={hideDealLostReasonModal} />}
    </Row>
  );
};

const mapStateToProps = (state: RootState) => ({
  canDownload: hasPermission(state)(PermissionName.EXPORT_DOWNLOAD),
  isFunnelChartHidden: getFunnelChartHidden(state),
  isOwner: isCurrentUserOwner(state),
});

const mapDispatchToProps = {
  downloadDealData,
  onSwitchView: switchView,
  toggleFunnelChartDisplay,
};

export default connect(mapStateToProps, mapDispatchToProps)(Toolbar);
